/**
 * A vault's storage tier as the vault records it — its provider, and the class
 * its file contents are kept in — and the one line the Vault screen and the
 * portal both show for it (ADR 0007; **Storage tier** in the desktop
 * glossary).
 *
 * The class is recorded as its request value, the key `lookupStorageClass`
 * finds a catalogue row by, and shown under the provider's own name for it.
 * The app invents no name for a class: one the catalogue lacks is shown as
 * recorded, and a provider the catalogue does not know, on the standard class
 * assumed of it, is named alone.
 */

import {
  STORAGE_PROVIDERS,
  UNRECOGNISED_PROVIDER_CLASS,
  canCreateVaultOn,
  lookupStorageClass,
  storageClassesOf,
} from "./storageClasses";

/**
 * Every provider a vault can record: the catalogue's, and `other` for an
 * S3-compatible provider it has no rows for. The desktop's `VaultProvider`
 * (`apps/desktop/src-tauri/src/storage_tier.rs`) serialises to these words,
 * and the API refuses a report carrying any other.
 */
export const VAULT_PROVIDERS = [...STORAGE_PROVIDERS, "other"] as const;

export type VaultProvider = (typeof VAULT_PROVIDERS)[number];

export function isVaultProvider(value: unknown): value is VaultProvider {
  return (VAULT_PROVIDERS as readonly unknown[]).includes(value);
}

/** Each provider as it names itself, in the catalogue's section headings. */
const PROVIDER_NAMES: Record<VaultProvider, string> = {
  aws: "Amazon S3",
  gcs: "Google Cloud Storage",
  azure: "Azure Blob Storage",
  backblaze: "Backblaze B2",
  wasabi: "Wasabi",
  other: "S3-compatible storage",
};

/** A provider's own name, alone — the half of `storageTierLine` that
 * `vaultDeleteProviderNote`'s class-specific retention sentence needs
 * without the class after it. */
export function vaultProviderName(provider: VaultProvider): string {
  return PROVIDER_NAMES[provider];
}

/**
 * Whether a vault on `provider` can have its storage tier changed after it
 * was created or attached (#842): the provider has more than one class a vault
 * can be created on. Amazon S3 and Azure, today; Google Cloud qualifies the day
 * `canCreateVaultOn` accepts more than its standard class. The desktop's
 * `VaultProvider::offers_a_choice_of_storage_tier` answers by the same rule.
 */
export function offersAChoiceOfStorageTier(provider: VaultProvider): boolean {
  if (provider === "other") return false;
  return storageClassesOf(provider).filter((c) => canCreateVaultOn(c).ok).length > 1;
}

/**
 * A vault's own recorded storage tier (#411, #617, ADR 0007): a provider
 * together with the class its file contents are kept in, as the provider's
 * request value. `storageClass` is `null` for the one provider whose class
 * takes none (Backblaze B2) — it is never present without `provider`, which
 * is what makes "a class only beside its provider" a rule this type states
 * once rather than one every caller re-derives. Defined here, once; every
 * other statement of it points here.
 */
export interface VaultStorageTier {
  readonly provider: VaultProvider;
  readonly storageClass: string | null;
}

/**
 * The reader: turns a vault's two loose wire fields — a provider and a class,
 * each independently optional — into a `VaultStorageTier` or none, the seam
 * where those two fields become the one value everything else reads.
 *
 * A missing field and a `null` one mean the same thing. A class recorded with
 * no provider is none: a class names nothing without its provider, so there
 * is no way to read one out on its own. A provider recorded with a missing or
 * `null` class reads as that provider with the class that takes none — which
 * is only ever right for a provider whose one class truly takes none
 * (Backblaze B2); every other provider's setup always records a class
 * alongside it, so no other provider reaches this reader without one.
 */
export function readVaultStorageTier(
  provider: VaultProvider | null | undefined,
  storageClass: string | null | undefined,
): VaultStorageTier | null {
  if (provider == null) return null;
  return { provider, storageClass: storageClass ?? null };
}

/**
 * "Amazon S3 · S3 Glacier Deep Archive": the provider, then the class under
 * the provider's own name. `tier.storageClass` is the recorded request value;
 * `null` is Backblaze B2's one class, which takes none.
 */
export function storageTierLine(tier: VaultStorageTier): string {
  const { provider, storageClass } = tier;
  const providerName = PROVIDER_NAMES[provider];
  const found = lookupStorageClass(provider, storageClass);

  if (found.kind === "class") return `${providerName} · ${found.storageClass.displayName}`;
  // The standard class assumed of a provider the catalogue does not know has
  // no name that provider gave it, and the app invents none.
  const unnamed =
    storageClass === null ||
    (found.kind === "unrecognised-provider" && storageClass === UNRECOGNISED_PROVIDER_CLASS);
  return unnamed ? providerName : `${providerName} · ${storageClass}`;
}
